import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape SUP-01 publishes, imported rather than restated.
import type { SupportCase } from "@tdelivr/api-client/generated/support/support-case.js";

import {
  caseDetail,
  casePage,
  fetchedNote,
  PER_FETCH,
  stateOf,
  statesPresent,
  topicCell,
  type CaseOrder,
} from "../cases.ts";
import { exportSheet, type ExportFormat } from "../export.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { GridPager, GridToolbar, NoMatch } from "./DataGrid.tsx";
import { PageHeading } from "./PageHeading.tsx";
import { RecordDetail } from "./RecordDetail.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * The case queue: open cases, most urgent first, with the state each is in.
 *
 * Built to `20 WP19 Support Console.dc.html`, page `cases`, on that file's one `gridUI()` — which
 * is byte-identical to the finance console's, checked rather than assumed along with `hdrUI`,
 * `badge`, `glyph`, `_rv` and `THEMEV`. So every measurement in the finance boards' headers applies
 * here unchanged.
 *
 * **ADR 0078 is what made this screen possible at all.** `docs/design/CONSOLE_SCREEN_SUPPORT.md`
 * recorded it as first blocked on "**every support-case operation is `/internal/v1`**", which the
 * gateway does not expose, so no browser could read a case. That record published
 * `GET /v1/tenants/{tenantId}/support-cases` and the by-identifier read beside it, with the
 * authorization unchanged — SUP-01's `assertCustomerOrAgent` was already right and had been since
 * the service was written. The board exists because an address was published, not because a
 * permission was loosened.
 *
 * **Three things the design draws are not drawn here, and all three are the same absence.**
 *
 * The page's own description is "Open cases sorted by urgency and **time to breach**". Its tiles
 * read `Breaching in 30 min · 3 · SLA` and `Breached · 1 · reply overdue`. Its rows read
 * `caution · reply due 12 min` and `failed · breached 6 min`. **Nothing on the platform publishes a
 * reply deadline** — no `dueAt`, no `slaBreachesAt`, no `respondBy`, on SUP-01 or anywhere else.
 * SUP-05 models escalation timers and is entirely `/internal/v1`, and a timer is not a deadline in
 * any case. So no countdown is drawn, no tile counting one is drawn, and no row is drawn as
 * breaching. A countdown computed here from `createdAt` and a policy this console invented would be
 * the worst outcome available: an agent would pace a shift by it. R-35.
 *
 * **No tiles at all, where the design draws four.** `Open · 48` is the other half of the same
 * problem: no listing on this platform publishes a total (R-04, measured across 70 non-internal
 * collection reads), and `48` beside a board holding 200 rows would be a count of what was fetched
 * wearing the label of a queue. The two SLA tiles have no source, and `Waiting on customer · 12`
 * would be a count over the same fetched window. The state filter already says which states are
 * present, from the same rows, without claiming to be a total.
 *
 * **The owner column is absent and the design's second line on a row with it.** The design writes
 * `active · assigned · J. Wekesa`. SUP-02 case-assignment holds that, and every one of its
 * operations is `/internal/v1` — so this board cannot read who owns a case, and `navigation.ts`
 * deliberately does not list SUP-02 as an owner of this page.
 *
 * **The primary action is drawn and refused.** `POST /internal/v1/.../support-cases` is internal,
 * and the design's form collects six fields against a published body of four — `Channel`,
 * `Linked trip, order or payment` and `What the customer said` have no field on it. Both halves are
 * on the button's own title.
 */

const caseApi = services.support__support_case;

export interface CaseQueueProps {
  readonly transport: Transport;
  readonly tenantId: string;
  /** The scopes on this session's token, which decide whose cases SUP-01 answers. */
  readonly scopes: ReadonlySet<string>;
  /** Innermost last: tenant, console, group, page. */
  readonly crumbs?: readonly string[];
  /**
   * Hand a case identifier to the timeline page, which the design's timeline is a view of.
   *
   * Not a control on the row, and not a button in the panel. The design draws neither, and its
   * detail footer has its own state-dependent action which `RecordDetail` draws from the state
   * class — see `detailActionFor`. This is the selection travelling with the navigation the rail
   * already does: the timeline page's own heading is "Everything that happened on case cs_4471",
   * so it is a view of *a* case and something has to say which.
   */
  readonly onSelectCase?: (caseId: string) => void;
}

export function CaseQueue({
  transport,
  tenantId,
  scopes,
  crumbs,
  onSelectCase,
}: CaseQueueProps): ReactNode {
  const [loaded, setLoaded] = useState<readonly SupportCase[] | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [selectedCaseId, setSelectedCaseId] = useState<string | undefined>(undefined);
  const [state, setState] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<CaseOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);

  const load = useCallback(async () => {
    const client = caseApi.createSupportCaseClient(transport);
    try {
      const answered = await client.listSupportCases(tenantId, String(PER_FETCH));
      setLoaded(answered.supportCases);
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, ["support-cases:read"]));
      setLoaded(undefined);
    }
  }, [transport, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = loaded ?? [];
  /* Across everything fetched, so the panel survives a page turn or a narrowed search. */
  const selectedCase = loaded?.find((row) => row.id === selectedCaseId);

  const view = useMemo(
    () => casePage(rows, { search, order, page, perPage }, state),
    [rows, search, order, page, perPage, state],
  );

  /*
   * No stale state. SUP-01's listing publishes no page-level instant, and a row's `updatedAt` is
   * when that case last moved rather than when this page was answered — so a quiet queue's newest
   * case is hours old and the board is not hours stale. R-35.
   */
  const viewState = failure ?? stateForList(loaded === undefined ? undefined : rows);

  const clearFilters = (): void => {
    setSearch("");
    setState(undefined);
    setPage(1);
  };

  const note = fetchedNote(loaded?.length, scopes.has("support-cases:agent"));

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="records__head">
      <span>Case</span>
      <span>Customer</span>
      <span>Topic</span>
      <span className="records__head--right">State</span>
    </div>
  );

  return (
    <div className="board-split">
      {/*
        The page and its detail panel in one positioned container, which is what docks the panel.

        Both docking rules are written against `.board-split`: at 1240 and above
        `.board-split:has(> .detail)` makes the panel a 420px second column, and below 1240 it is
        `position: absolute; inset: 0 0 0 auto`, positioned against `.board-split`'s own
        `position: relative`. This board had neither — the panel was rendered inside the page, and
        on seven of these twelve boards inside the closing-note callout, where it was a flex item
        beside an `info` glyph and a sentence.

        `Settlement.tsx` and `Invoices.tsx` were already this shape, which is why they are the
        shape copied here rather than a new one. Found by the census in
        `tooling/scripts/lib/detail-panels.mjs`: this panel had never been photographed, so
        nothing compared it against the design it is built from. Which docking is right at all is
        Q-297.
      */}
      <section className="page records cases">
        <PageHeading
          crumbs={crumbs ?? []}
          title="Case queue"
          source="SUP-01"
          description="Open cases sorted by urgency and time to breach, with who owns each one."
          action={
            <button
              type="button"
              className="records__primary"
              /*
               * Drawn and refused, for two reasons that are worth keeping apart.
               *
               * The route is internal. `POST /internal/v1/tenants/{tenantId}/support-cases` is the
               * only way to open a case and the shared front door does not serve `/internal/v1` —
               * ADR 0039 §1 draws that line deliberately, because a workload posting over the
               * internal network is not the same caller as a browser.
               *
               * And the published body could not take the form anyway. It is four fields —
               * `customerReference`, `subject`, `category`, `priority` — and the design's form
               * collects six: `Channel (select)`, `Linked trip, order or payment (text)` and
               * `What the customer said (area)` have no field on it. The last of those is the one
               * that matters most: it is what the customer actually said, and it would be collected
               * and dropped.
               */
              disabled
              title="POST /internal/v1/tenants/{tenantId}/support-cases is an internal route the shared front door does not serve (ADR 0039 §1), and its published body has no field for the design's Channel, Linked trip/order/payment or What the customer said. See R-35."
            >
              <span aria-hidden="true" className="records__primary-glyph">
                add
              </span>
              New case
            </button>
          }
        />

        <div className="records__panel">
          <GridToolbar
            idPrefix="cases"
            search={search}
            onSearch={(next) => {
              setSearch(next);
              setPage(1);
            }}
            state={state}
            onState={(next) => {
              setState(next);
              setPage(1);
            }}
            states={statesPresent(rows)}
            order={order}
            onOrder={setOrder}
            onExport={(format: ExportFormat) => {
              exportSheet(sheetFor(view.all), format, "cases");
            }}
          />

          <StateView
            state={viewState}
            purpose="Cases appear here as soon as one is opened, from any channel."
            onRetry={() => {
              void load();
            }}
            skeleton={{
              head,
              columns: 4,
              rowClassName: "records__row",
              // Both strings come from the trail the shell already draws, so the
              // sentence can never disagree with the breadcrumb above it.
              page: crumbs?.at(-1) ?? "this page",
              tenant: crumbs?.[0] ?? "this tenant",
            }}
          >
            {head}
            <div className="records__rows">
              {view.rows.map((supportCase) => {
                const shown = stateOf(supportCase);
                return (
                  <div
                    key={supportCase.id}
                    data-selected={supportCase.id === selectedCaseId ? "true" : undefined}
                    role="button"
                    tabIndex={0}
                    aria-pressed={supportCase.id === selectedCaseId}
                    aria-label={`Open case ${supportCase.id}`}
                    onClick={() => {
                      setSelectedCaseId(supportCase.id);
                      onSelectCase?.(supportCase.id);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      setSelectedCaseId(supportCase.id);
                      onSelectCase?.(supportCase.id);
                    }}
                    className={
                      shown.classKey === "human" ? "records__row cases__row--human" : "records__row"
                    }
                  >
                    <span className="records__lead" title={supportCase.id}>
                      {supportCase.id}
                    </span>
                    <span
                      className="records__cell records__cell--mono"
                      /*
                       * The reference, drawn as a reference. The design draws a display name and a
                       * masked phone number; SUP-01 publishes neither, CUS-01 holds the first, and a
                       * per-row join to it is a second service on a board. The title says so once
                       * per row rather than the cell pretending.
                       */
                      title={`SUP-01 publishes customerReference. The design draws a display name and a masked phone number; neither is published here and CUS-01 holds the first. See R-35.\n${supportCase.customerReference}`}
                    >
                      {supportCase.customerReference}
                    </span>
                    <span
                      className="records__cell"
                      title={[
                        `subject ${supportCase.subject}`,
                        `category ${supportCase.category}`,
                        `priority ${supportCase.priority}`,
                        "The design draws the trip, order or payment this case is about. Nothing links a case to one — see R-35.",
                      ].join("\n")}
                    >
                      {topicCell(supportCase)}
                    </span>
                    <span className="records__state">
                      <StateBadge
                        code="SUP-01"
                        status={supportCase.status}
                        classKey={shown.classKey}
                        label={shown.label}
                        title={`SUP-01 publishes ${supportCase.status}. ${describeStatus(supportCase)}`}
                      />
                    </span>
                  </div>
                );
              })}
            </div>
            {view.total === 0 ? <NoMatch onClear={clearFilters} /> : null}
            <GridPager
              idPrefix="cases"
              page={page}
              pageCount={view.pageCount}
              perPage={perPage}
              onPerPage={(next) => {
                setPerPage(next);
                setPage(1);
              }}
              total={view.total}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => {
                if (page < view.pageCount) setPage(page + 1);
              }}
              /*
               * Never more. SUP-01's listing publishes no cursor, so there is nothing to fetch beyond
               * the limit and a Next that offered to would do nothing — the one shape this
               * repository keeps paying for. The note under the grid says it in words.
               */
              more={false}
              fetching={false}
            />
          </StateView>

          {note === undefined ? null : (
            <p className="records__bounded" role="status">
              {note}
            </p>
          )}
        </div>

        {/* The design's closing note, word for word, in its own `info` kind. */}
        <div className="records__note records__note--info">
          <span aria-hidden="true" className="records__note-glyph">
            info
          </span>
          <span>Cases pull in trip, order, payment and message history automatically.</span>
        </div>
      </section>

      {/* The selected row, in full, with the one link a case row has. */}
      {selectedCase === undefined ? null : (
        <RecordDetail
          record={caseDetail(selectedCase)}
          open
          onClose={() => setSelectedCaseId(undefined)}
        />
      )}
    </div>
  );
}

/**
 * What a status means, for the badge's title.
 *
 * One sentence per published value. The pair that matters is the two pendings: they are the whole
 * difference between a case an agent is late on and one they are not, and the words `pending
 * customer` and `pending support` are easy to read the wrong way round.
 */
function describeStatus(supportCase: SupportCase): string {
  switch (supportCase.status) {
    case "open":
      return "It is open and nobody has moved it yet.";
    case "pending_customer":
      return "The next move is the customer's. SUP-01 publishes no reply deadline, so nothing here says how long they have.";
    case "pending_support":
      return "The next move is support's. SUP-01 publishes no owner, and SUP-02 — which holds assignments — is entirely internal, so nothing here says whose.";
    case "resolved":
      return "It has been answered and not yet closed.";
    case "closed":
      return "It is closed. SUP-01 publishes no reopen count, which is what the design's Satisfaction page would need.";
  }
}

/**
 * The rectangle an export writes: the columns on screen, in the words on screen.
 *
 * Two columns more than the board. `Priority` is the field the whole board is ordered by, and a row
 * read out of a file months later has no "most urgent first" to be read against; `Opened` is the
 * service's own secondary order. Both are published and required, and a spreadsheet is read without
 * hover.
 */
function sheetFor(rows: readonly SupportCase[]): {
  header: readonly string[];
  rows: readonly (readonly string[])[];
} {
  return {
    header: ["Case", "Customer", "Topic", "State", "Priority", "Opened"],
    rows: rows.map((supportCase) => [
      supportCase.id,
      supportCase.customerReference,
      topicCell(supportCase),
      stateOf(supportCase).label,
      supportCase.priority,
      supportCase.createdAt,
    ]),
  };
}
